import React, { useEffect, useMemo, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import Select from 'react-select';
import { ModalComponent } from 'new/routes/RouterManager';
import { ModalOutProps } from 'new/routes/ModalDefault';
import { addDomainItem } from 'js/actions';
import { showAlert } from 'new/stores/showAlert/action';
import { useModals } from 'new/hooks/useModals';
import { isOwner } from 'js/lib/ACLUtils';
import { Button } from '../Button';
import { useMarketplaceDomains } from 'new/hooks/adapters/useMarketplaceDomains';
import { SelectInput, SelectOption } from 'js/components/elements/rselect/ReactSelectUtils';
import useAppDispatch from 'js/hooks/useAppDispatch';

interface ModalAddToMarketplaceProps {
  data: {
    id: number;
    name: string;
  };
}

const selectStyles = { menuPortal: (base: any) => ({ ...base, zIndex: 1300 }) };
const description =
  'Generate a Request to add this Nexset to a Marketplace Domain. If you are the Data Custodian, Nexset will automatically add itself to the selected Domain.';

export const ModalAddToMarketplace: ModalComponent = (
  props: ModalAddToMarketplaceProps & ModalOutProps,
) => {
  const { data: dataset, setModalActions, setFooter } = props;
  const dispatch = useAppDispatch();
  const { closeModal } = useModals();
  const isDatasetOwner = isOwner(dataset);
  const { domainEntitiesById } = useMarketplaceDomains();

  const domainItems = useMemo(() => {
    return domainEntitiesById.map((domain) => ({
      label: domain?.name,
      value: domain?.id,
    }));
  }, [domainEntitiesById]);

  const [selectedDomain, setSelectedDomain] = useState({ value: undefined });

  useEffect(() => {
    if (isDatasetOwner) {
      setModalActions({
        ok: {
          text: 'Add',
          onClick: onSubmit,
          disabled: selectedDomain.value === undefined,
        },
      });
    } else {
      setFooter(
        <Stack
          direction="row-reverse"
          justifyContent="space-between"
          alignItems="center"
          padding="16px"
        >
          <Button
            onClick={() => closeModal(ModalAddToMarketplace.modalConfig.name)}
            variant="outlined"
          >
            Close
          </Button>
        </Stack>,
      );
    }
  }, [selectedDomain.value]);

  const onChangeDomain = (option: React.SetStateAction<{ value: any }>) => {
    setSelectedDomain(option);
  };

  const onSubmit = () => {
    dispatch(addDomainItem(selectedDomain?.value, dataset?.id)).then(() => {
      dispatch(
        showAlert({
          title: 'Marketplace Listing Request sent successfully',
          severity: 'success',
        }),
      );
      closeModal(ModalAddToMarketplace.modalConfig.name);
    });
  };

  return (
    <>
      {isDatasetOwner ? (
        <Stack gap="32px">
          <Typography>{description}</Typography>
          <Stack gap="12px">
            <Stack>
              <Typography>NEXSET</Typography>
              <Typography>{dataset.name}</Typography>
            </Stack>
            <Stack>
              <Typography>Select a Data Domain</Typography>
              <Select
                options={domainItems}
                value={selectedDomain}
                onChange={onChangeDomain}
                components={{ Input: SelectInput, Option: SelectOption }}
                menuPortalTarget={document.body}
                styles={selectStyles}
              />
            </Stack>
          </Stack>
        </Stack>
      ) : (
        <Stack>
          <Typography>
            You do not have owner permissions to add this Nexset to the Private Data Product
            Marketplace. Please contact the Nexset owner.
          </Typography>
        </Stack>
      )}
    </>
  );
};

ModalAddToMarketplace.modalConfig = {
  name: 'ModalAddToMarketplace',
  title: 'Add Nexset to Marketplace',
  fullWidth: true,
  maxWidth: 'sm',
};
